import React, { useState, useContext, useEffect } from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { PrismicRichText } from "@prismicio/react";
import { useInView } from "react-intersection-observer";
import { format, isValid, parseISO } from "date-fns";
import { fr, enGB } from "date-fns/locale";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

// SEO
import { PageSeo } from "../components/global/page-seo";

// Components
import { DetermineModule } from "../components/utils/determine-module";

// Context
import { CurrentLanguage } from "../components/context/current-language";
import { HeaderColor } from "../components/context/header-color";
import { PageColor } from "../components/context/page-color";
import { PageContext } from "../components/context/page-context";

const Content = styled.div`
  padding: 100px 40px 90px 40px;

  @media (max-width: 800px) {
    padding: 31px 25px;
  }

  & .event-container {
    margin: 0 auto;
    max-width: 1280px;

    & .featured-img {
      aspect-ratio: 5/7;
      grid-column: 1 / 7;
      border: 39px solid #fff;
      box-sizing: border-box;

      @media (max-width: 800px) {
        margin: 0 auto;
        grid-column: 1 / 13;
        margin-bottom: 50px;
      }
    }

    & .text-container {
      grid-column: 8 / 13;

      @media (max-width: 800px) {
        grid-column: 1 / 13;
      }

      & .title-bar {
        display: flex;
        flex-direction: row;
        gap: 4px;
        margin: 0 0 22px 0;

        & p {
          margin: 0;
        }

        & .spacer {
          margin: 0 10px;
        }
      }

      & .title {
        & h1 {
          @media (max-width: 999px) {
            font-size: 30px;
            line-height: 40px;
          }
        }
      }

      & .text {
        margin: 20px 0 0 0;

        & p {
          font-size: 18px;
          line-height: 30px;

          text-align: justify;

          @media (max-width: 999px) {
            font-size: 14px;
            line-height: 24px;
          }
        }
      }
    }
  }
`;

const SliceZone = styled.div`
  & .product-row-carousel {
    background-color: #ffffff;
  }
`;

const Event = ({ data }) => {
  const [currentLanguage, setCurrentLanguage] = useContext(CurrentLanguage);

  const [pageColor, setPageColor] = useContext(PageColor);
  const [headerColor, setHeaderColor] = useContext(HeaderColor);
  const [pageContext, setPageContext] = useContext(PageContext);

  useEffect(() => {
    setPageColor(`#efefef`);
    setHeaderColor(`#efefef`);
  }, []);

  const formatEventDate = (dateBegins, dateEnds, labels, lang = "fr-fr") => {
    const locale = lang === "fr-fr" ? fr : enGB;
    const pattern = lang === "fr-fr" ? "dd-MM-yyyy" : "yyyy-MM-dd";

    const begins = parseISO(dateBegins);
    const ends = dateEnds ? parseISO(dateEnds) : null;

    if (!isValid(begins)) return null;

    const beginsFormatted = format(begins, pattern, { locale });

    if (!ends || !isValid(ends) || dateBegins === dateEnds) {
      return `${beginsFormatted}`;
    }

    const endsFormatted = format(ends, pattern, { locale });

    return `${labels?.date_from || "from"} ${beginsFormatted} ${
      labels?.date_to || "to"
    } ${endsFormatted}`;
  };

  const labelsEntries = data.prismicSiteLabels?.data?.labels || [];

  const labels = Object.fromEntries(
    labelsEntries
      .filter((item) => item.key && item.value)
      .map((item) => [item.key, item.value])
  );

  const categoryTextMap = {
    Paris: "rue de Beaune",
    "New-York": "NYDC",
  };

  // Check if data exists
  if (!data?.prismicEvent?.data) {
    return <div>Event not found</div>;
  }

  return (
    <div>
      <Content>
        <div className="event-container grid-12">
          <img
            className="featured-img"
            src={data.prismicEvent.data.featured_img?.url}
            alt={data.prismicEvent.data.featured_img?.alt || ""}
          />

          <div className="text-container">
            <div className="title-bar tag gotham-bold uppercase">
              <p>
                {formatEventDate(
                  data.prismicEvent.data.date_begins,
                  data.prismicEvent.data.date_ends,
                  labels,
                  data.prismicEvent.lang
                )}
              </p>{" "}
              <p>|</p>
              <p>
                {(() => {
                  if (
                    !data.prismicEvent.data?.categories ||
                    data.prismicEvent.data.categories.length === 0
                  ) {
                    return labels?.offsite || "";
                  }

                  const categories = data.prismicEvent.data.categories
                    .map((item) => {
                      const categoryName =
                        item.category?.document?.data?.title?.text;
                      return categoryName
                        ? categoryTextMap[categoryName] || categoryName
                        : null;
                    })
                    .filter(Boolean)
                    .join(", ");

                  return categories || labels?.offsite || "";
                })()}
              </p>
            </div>
            <h1>{data.prismicEvent.data.title?.text}</h1>
            <p>{data.prismicEvent.data?.excerpt}</p>
          </div>
        </div>
      </Content>
      <SliceZone>
        {data.prismicEvent.data.body?.map((content, index) => (
          <DetermineModule
            key={`event_slice_${index}_${data.prismicEvent.id}`}
            content={content}
            index={index}
            pageType="event"
            currentLanguage={currentLanguage}
            labels={labels}
          />
        ))}
      </SliceZone>
    </div>
  );
};

export const query = graphql`
  query EventQuery($uid: String!, $lang: String!) {
    prismicEvent(uid: { eq: $uid }, lang: { eq: $lang }) {
      _previewable
      alternate_languages {
        uid
        type
        lang
      }
      id
      lang
      url
      type
      data {
        title {
          text
        }
        date_begins
        date_ends
        excerpt
        featured_img {
          url
          alt
        }
        categories {
          category {
            document {
              ... on PrismicGalleriesSingle {
                data {
                  title {
                    text
                  }
                  galleries_address
                  galleries_city
                  galleries_country
                  galleries_phone
                }
                url
                uid
                type
                lang
              }
            }
          }
        }
        body {
          ... on PrismicEventDataBodyProductRowCarousel {
            id
            slice_type
            primary {
              product_row_title {
                richText
              }
            }
            items {
              product {
                document {
                  ... on PrismicProduct {
                    id
                    url
                    data {
                      thumbnail {
                        gatsbyImageData(aspectRatio: 0.71)
                        alt
                      }
                      title {
                        text
                      }
                      price_on_demand
                      shopify_product {
                        title
                        handle
                        id
                        variants {
                          price
                        }
                      }
                    }
                  }
                }
                url
                id
              }
            }
          }
          ... on PrismicEventDataBodyEditorialRow {
            id
            slice_type
            primary {
              editorial_row_title {
                richText
              }
            }
            items {
              article {
                document {
                  ... on PrismicArticle {
                    id
                    url
                    data {
                      title {
                        richText
                      }
                      excerpt_text {
                        richText
                      }
                      thumbnail {
                        alt
                        dimensions {
                          height
                          width
                        }
                        gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 800)
                      }
                      categories {
                        category {
                          document {
                            ... on PrismicJournalCategory {
                              id
                              url
                              data {
                                title {
                                  text
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicEventDataBodyEditorialImage {
            id
            slice_type
            primary {
              image {
                alt
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
                dimensions {
                  height
                  width
                }
              }
            }
          }
          ... on PrismicEventDataBodyEditorialImages {
            id
            slice_type
            primary {
              editorial_image_left {
                alt
                dimensions {
                  height
                  width
                }
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
              }
              editorial_image_right {
                dimensions {
                  height
                  width
                }
                alt
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
              }
              image_left_background
              image_right_background
              invert_layout
            }
          }
          ... on PrismicEventDataBodyEditorialImageWithText {
            id
            slice_type
            primary {
              image {
                alt
                gatsbyImageData(layout: FULL_WIDTH, srcSetMaxWidth: 1000)
                fluid {
                  aspectRatio
                }
                dimensions {
                  height
                  width
                }
              }
              text {
                richText
              }
            }
          }
          ... on PrismicEventDataBodyEditorialText {
            id
            slice_type
            primary {
              editorial_text {
                richText
              }
            }
          }
          ... on PrismicEventDataBodyNewsletter {
            id
            slice_type
            primary {
              newsletter {
                document {
                  ... on PrismicNewsletterSignup {
                    id
                    data {
                      background_color
                      email_placeholder
                      newsletter_text {
                        richText
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicEventDataBodyVideo {
            id
            slice_type
            primary {
              video {
                type
                html
                embed_url
                provider_name
                id
              }
              video_size
            }
          }
          ... on PrismicEventDataBodyCustomCarousel {
            id
            slice_type
            primary {
              carousel_title {
                text
              }
              button_text
            }
            items {
              content_link {
                document {
                  ... on PrismicEvent {
                    id
                    url
                    type
                    lang
                    data {
                      title {
                        text
                      }
                      date_begins
                      date_ends
                      featured_img {
                        alt
                        gatsbyImageData(aspectRatio: 0.71)
                      }
                      categories {
                        category {
                          uid
                        }
                      }
                    }
                  }
                  ... on PrismicProject {
                    id
                    url
                    type
                    lang
                    data {
                      title {
                        text
                      }
                      project_type
                      project_type_french
                      thumbnail {
                        alt
                        gatsbyImageData(aspectRatio: 0.71)
                      }
                      categories {
                        category {
                          document {
                            ... on PrismicActivityCategory {
                              id
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                  ... on PrismicArticle {
                    id
                    url
                    type
                    lang
                    data {
                      title {
                        text
                      }
                      thumbnail {
                        alt
                        gatsbyImageData(aspectRatio: 0.71)
                      }
                      categories {
                        category {
                          document {
                            ... on PrismicJournalCategory {
                              id
                              url
                            }
                            ... on PrismicActivityCategory {
                              id
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    prismicSiteLabels(uid: { eq: "default" }, lang: { eq: $lang }) {
      data {
        labels {
          key
          value
        }
      }
    }
  }
`;

export default withPrismicPreview(Event);
